import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { UserPlusIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import Image from '@/components/ui/Image';
import { Skeleton } from '@/components/ui/skeleton';
import { useMySpaceInvites, useRespondToSpaceInvite, type SpaceInviteAnswer } from '@/hooks/queries/useSpaceInvitations';
import { ApiError } from '@/lib/api';
import { useRouter } from '@/lib/router';
import { SPACE_KIND_LABELS, spaceHref } from '@/lib/spaces/navigation';
import { track } from '@/lib/telemetry';
import type { MySpaceInvite } from '@/types/spaces';

export function SpaceInvitations({ surface = 'home', onAccepted }: { surface?: 'home' | 'bell'; onAccepted?: () => void }) {
  const query = useMySpaceInvites();
  if (!query.enabled) return null;
  if (!query.isLoading && !query.error && !query.data?.length) return null;
  const handleRetry = () => { void query.mutate().catch(() => undefined); };
  const renderInvite = (invite: MySpaceInvite) => <SpaceInvitation key={invite.id} invite={invite} surface={surface} onAccepted={onAccepted} />;
  return <section aria-label="Convites para espaços" className={surface === 'home' ? 'rounded-3xl border border-border-subtle bg-surface p-5' : 'max-h-96 overflow-y-auto border-b border-border-subtle p-4'}>
    <h2 className="flex items-center gap-2 text-sm font-semibold text-text-primary"><UserPlusIcon aria-hidden className="size-4 text-primary" />Convites para espaços</h2>
    <InviteListState loading={query.isLoading} error={query.error} onRetry={handleRetry} />
    {!query.error && <ul className="mt-3 divide-y divide-border-subtle">{query.data?.map(renderInvite)}</ul>}
  </section>;
}

function InviteListState({ loading, error, onRetry }: { loading: boolean; error?: Error; onRetry: () => void }) {
  if (loading) return <div role="status" aria-label="Carregando convites" aria-busy="true" className="mt-3"><Skeleton className="h-20 w-full rounded-xl" /></div>;
  if (!error) return null;
  return <div role="alert" className="mt-3 space-y-2 text-sm text-text-secondary"><p>Não foi possível carregar seus convites.</p><Button type="button" variant="outline" onClick={onRetry}>Tentar novamente</Button></div>;
}

/**
 * One invite to answer. Accepting is a two-step when the account has a photo: the other members will see it
 * next to the person's name from then on, so the person is told and agrees BEFORE anything is sent — entering
 * IS agreeing (there is no way to join and keep the photo back; ruled 2026-09-22), and the answer carries the
 * photo for the API to store on the membership. Without a photo there is nothing to agree to and the accept
 * goes straight through, as before.
 */
function SpaceInvitation({ invite, surface, onAccepted }: { invite: MySpaceInvite; surface: 'home' | 'bell'; onAccepted?: () => void }) {
  const router = useRouter();
  const { user } = useUser();
  // Clerk always fills `imageUrl` (a generated avatar when the person never set a picture); only `hasImage` says the
  // account has a photo of its own — the one thing there is to agree to share.
  const photoUrl = user?.hasImage && user.imageUrl ? user.imageUrl : null;
  const { trigger, isMutating } = useRespondToSpaceInvite(invite.id);
  const [error, setError] = useState<string | null>(null);
  const [decidingPhoto, setDecidingPhoto] = useState(false);
  const respond = async (answer: SpaceInviteAnswer) => {
    setError(null);
    try {
      const space = await trigger(answer);
      if (space) { track('space.invite.accepted', { surface }); onAccepted?.(); router.push(spaceHref(space.id)); }
      else toast.success('Convite recusado.');
    } catch (cause) {
      const message = cause instanceof ApiError && [400, 403, 404, 409].includes(cause.status)
        ? cause.message : 'Não foi possível responder ao convite. Tente novamente.';
      setError(message);
      setDecidingPhoto(false);
      toast.error(message);
    }
  };
  const handleAccept = () => { if (photoUrl) setDecidingPhoto(true); else void respond({ answer: 'accept', photoUrl: null }); };
  const handleDecline = () => { void respond({ answer: 'decline' }); };
  const handleShowPhoto = () => { void respond({ answer: 'accept', photoUrl }); };
  const handleBack = () => setDecidingPhoto(false);
  return <li aria-busy={isMutating} className="space-y-2 py-3 first:pt-0 last:pb-0">
    <p className="break-words text-sm font-semibold text-text-primary">{invite.spaceName}</p>
    <p className="text-xs text-text-secondary">{invite.invitedByName ?? 'Uma pessoa'} convidou você · {SPACE_KIND_LABELS[invite.spaceKind]}</p>
    <p className="text-xs text-text-secondary">{invite.pct === null ? 'Percentual definido ao entrar' : `Parte combinada: ${invite.pct.toLocaleString('pt-BR')}%`} · {invite.role === 'editor' ? 'Editor' : 'Leitura'}</p>
    <p className="text-xs text-text-secondary">Aceitar não compartilha seus lançamentos. Só entra o que você enviar.</p>
    {decidingPhoto && photoUrl
      ? <PhotoConsent spaceName={invite.spaceName} photoUrl={photoUrl} busy={isMutating} onShow={handleShowPhoto} onBack={handleBack} />
      : <div className="flex flex-wrap gap-2"><Button type="button" size="sm" disabled={isMutating} onClick={handleAccept} aria-label={`Aceitar convite para ${invite.spaceName}`}>Aceitar</Button><Button type="button" size="sm" variant="outline" disabled={isMutating} onClick={handleDecline} aria-label={`Recusar convite para ${invite.spaceName}`}>Recusar</Button></div>}
    <InviteActionError error={error} />
  </li>;
}

function PhotoConsent({ spaceName, photoUrl, busy, onShow, onBack }: {
  spaceName: string; photoUrl: string; busy: boolean; onShow: () => void; onBack: () => void;
}) {
  return <div role="group" aria-label={`Sua foto em ${spaceName}`} className="space-y-3 rounded-2xl border border-border-subtle bg-surface-sunken p-3">
    <div className="flex items-start gap-3">
      <Image src={photoUrl} alt="Sua foto de perfil" width={40} height={40} className="size-10 shrink-0 rounded-full object-cover" data-testid="photo-consent-preview" />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-semibold text-text-primary">Ao entrar, sua foto aparece para as pessoas de {spaceName}.</p>
        <p className="text-xs text-text-secondary">Quem está no espaço vê a foto da sua conta ao lado do seu nome, em tudo que for seu ali. Entrar é concordar com isso.</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      <Button type="button" size="sm" disabled={busy} onClick={onShow} className="h-auto max-w-full whitespace-normal">Entrar e mostrar minha foto</Button>
      <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onBack}>Voltar</Button>
    </div>
  </div>;
}

export function InviteActionError({ error }: { error: string | null }) {
  if (!error) return null;
  return <p role="alert" className="text-xs text-error-text">{error}</p>;
}
